import React from 'react';
import { Card } from 'antd';
import { SkBar, SkTabsBar } from './_primitives';

const UsersHeader = () => (
  <div className="flex flex-col gap-2 mb-4 sm:mb-6">
    <div className="flex items-center gap-2">
      <SkBar h="h-3" w="w-16" />
      <SkBar h="h-3" w="w-2" />
      <SkBar h="h-3" w="w-24" />
    </div>
    <SkBar h="h-7" w="w-64" className="max-w-full mt-1" />
    <SkBar h="h-3" w="w-80" className="max-w-full" />
  </div>
);

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-11 h-11 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-6" w="w-20" />
        <SkBar h="h-2" w="w-24" />
      </div>
    </div>
  </Card>
);

const FilterBar = () => (
  <div className="flex flex-col sm:flex-row gap-3 mb-4">
    <SkBar h="h-10" w="w-full sm:flex-1 sm:max-w-md" className="rounded-md" />
    <SkBar h="h-10" w="w-full sm:w-36" className="rounded-md flex-shrink-0" />
  </div>
);

const USER_COLS = [
  { label: 'w-12' },  // User
  { label: 'w-16' },  // Contact
  { label: 'w-8' },   // Tier
  { label: 'w-24' },  // Points Balance
  { label: 'w-12' },  // Status
  { label: 'w-24' },  // Member Since
  { label: 'w-12' },  // Actions
];

const UsersTable = () => (
  <div className="overflow-x-auto">
    <div className="min-w-[900px]">
      {/* Header row */}
      <div className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_0.6fr] gap-3 pb-3 border-b border-gray-200 mb-3">
        {USER_COLS.map((c, i) => (
          <SkBar key={i} h="h-3" w={c.label} />
        ))}
      </div>

      {/* Data rows */}
      {Array.from({ length: 8 }).map((_, r) => (
        <div
          key={r}
          className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_0.6fr] gap-3 py-3 border-b border-gray-100 last:border-b-0 items-center"
        >
          {/* User: avatar + name + id */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <SkBar h="h-3" w="w-32" />
              <SkBar h="h-2" w="w-20" />
            </div>
          </div>

          {/* Contact: email + phone */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <SkBar h="h-3" w="w-40" />
            <SkBar h="h-2" w="w-24" />
          </div>

          {/* Tier pill */}
          <SkBar h="h-5" w="w-16" className="rounded-md" />

          {/* Points balance */}
          <SkBar h="h-3" w="w-10" />

          {/* Status switch */}
          <SkBar h="h-6" w="w-16" className="rounded-full" />

          {/* Member since date */}
          <SkBar h="h-3" w="w-16" />

          {/* Action icon button */}
          <SkBar h="h-8" w="w-8" className="rounded-md" />
        </div>
      ))}
    </div>
  </div>
);

const TablePagination = () => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-24" />
    <div className="flex items-center gap-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <SkBar key={i} h="h-8" w="w-8" className="rounded-md" />
      ))}
    </div>
    <SkBar h="h-8" w="w-24" className="rounded-md" />
  </div>
);

/**
 * UsersPageLoader — matches User Management layout:
 *  breadcrumb + title/subtitle header · 4 stat cards (icon-left) ·
 *  card with 3 tabs · search + advanced-filters · user table · pagination.
 */
const UsersPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <UsersHeader />

    {/* 4 stat cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    {/* Tabs + filters + table + pagination */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <SkTabsBar tabs={3} />
      <FilterBar />
      <UsersTable />
      <TablePagination />
    </Card>
  </div>
);

export default UsersPageLoader;
